import { create } from 'zustand';
import { isNativeApp, SdDevices, type ReceivedPayment } from './native';

/**
 * Payment alerts, like the SME app's soundbox: the Android app hears "money received" notifications from
 * Google Pay, PhonePe, Paytm, BHIM and bank SMS (PaymentListener.java). The phone says each one out loud,
 * even with the app closed; here we keep the list and let a waiting UPI bill complete by itself.
 */
interface State { granted: boolean | null; list: ReceivedPayment[] }
export const usePaymentAlerts = create<State>(() => ({ granted: null, list: [] }));

type Fn = (p: ReceivedPayment) => void;
const subs = new Set<Fn>();
/** Hear each new payment (e.g. the payment screen waiting for a UPI amount). Returns unsubscribe. */
export function onPayment(fn: Fn) { subs.add(fn); return () => { subs.delete(fn); }; }

function received(p: ReceivedPayment) {
  usePaymentAlerts.setState((s) => ({ list: [p, ...s.list.filter((x) => x.id !== p.id)].slice(0, 100) }));
  for (const fn of subs) fn(p);
}

export async function refreshPaymentAlerts() {
  if (!isNativeApp()) return;
  const [{ granted }, { payments }] = await Promise.all([SdDevices.paymentAlerts(), SdDevices.recentPayments()]);
  usePaymentAlerts.setState({ granted, list: payments });
}

export function openPaymentAlertSettings() {
  if (isNativeApp()) void SdDevices.openPaymentAlertSettings();
}

let started = false;
export function startPaymentAlerts() {
  if (started) return;
  started = true;
  if (isNativeApp()) {
    void SdDevices.addListener('payment', received);
    void refreshPaymentAlerts().catch(() => {});
    // Coming back from Android's "Notification access" screen.
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void refreshPaymentAlerts().catch(() => {}); });
  }
  // Tests and the browser preview: pretend a UPI app said money came in.
  if (import.meta.env.DEV) {
    (window as unknown as { __sdPayment: (amount: number, payer?: string, app?: string) => void }).__sdPayment = (amount, payer = '', app = 'Google Pay') => {
      usePaymentAlerts.setState({ granted: true });
      received({ id: crypto.randomUUID(), amount, payer, app, ts: Date.now() });
    };
  }
}

/** Payments heard today, and their total. */
export function todayTotal(list: ReceivedPayment[]) {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const today = list.filter((p) => p.ts >= d.getTime());
  return { today, total: today.reduce((a, p) => a + p.amount, 0) };
}
